import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Navigate, useNavigate } from "react-router-dom";
import clsx from "clsx";
import { FiArrowDown } from "react-icons/fi";
import useOriChat from "apps/user/learner/hooks/use-ori-chat";
import useOriHistory from "apps/user/learner/hooks/use-ori-history";
import useScrollPin from "apps/user/learner/hooks/use-scroll-pin";
import useOriDisclaimer from "apps/user/learner/hooks/use-ori-disclaimer";
import useWebOriEnabled from "apps/user/learner/hooks/use-web-ori-enabled";
import useOriQaSession from "apps/user/learner/hooks/use-ori-qa-session";
import useFeatureFlags, {
  FEATURE_FLAGS,
} from "apps/user/learner/hooks/use-feature-flags";
import { PageLoading } from "apps/user/learner/components/page-container";
import { toast } from "react-toastify";
import { useAuth } from "providers/auth";
import { trackEvent } from "utility/mixpanel";
import { useTranslation } from "lib/i18n/use-translation";
import { OriAvatar, ORI_ORB_CROP_SCALE } from "components/ori";
import { Button } from "components/ui/Button";
import OriConnectionBanner from "./components/OriConnectionBanner";
import OriMessageList from "./components/OriMessageList";
import OriComposer from "./components/OriComposer";
import OriHistoryButton from "./components/OriHistoryButton";
import OriQaPanelButton from "./components/OriQaPanelButton";
import OriHistoryPanel from "./components/OriHistoryPanel";
import OriDisclaimerModal from "./components/OriDisclaimerModal";
import OriQaModeToggle from "./components/OriQaModeToggle";
import OriQaDrawer from "./components/OriQaDrawer";
import OriStarterPills, {
  type StarterPillOption,
} from "./components/OriStarterPills";
import { GENERIC_STARTER_ICONS } from "./components/oriChatStarterIcons";
import {
  GENERIC_STARTER_IDS,
  selectDynamicStarters,
  selectGenericStarters,
  shuffled,
} from "./chat-starters";
import { refreshOriStarterMessage } from "apps/user/learner/lib/ori-starter-message";
import type { OptionsSource } from "./types";
import styles from "./styles.module.scss";

/** A row of starters: a heading message's, or the generic set (no message). */
interface StarterSet {
  messageId: string | null;
  options: StarterPillOption[];
  source: OptionsSource | "generic";
}

const HEADER_DATE_FORMAT = new Intl.DateTimeFormat(undefined, {
  day: "numeric",
  month: "long",
});

function formatHeaderDate(iso: string): string {
  return HEADER_DATE_FORMAT.format(new Date(iso));
}

const OriChat: React.FC = () => {
  const { t } = useTranslation(["ori"]);
  // Read acceptance first: the chat connection is gated on it, so nothing
  // reaches Coach (and no entry-point starter is consumed) until the user has
  // accepted the disclaimer. On accept, `hasAccepted` flips, the socket opens,
  // and the stashed starter is sent against the now-visible chat.
  const { hasAccepted, isAccepting, acceptDisclaimer } = useOriDisclaimer();
  const oriQaSession = useOriQaSession();
  const {
    messages,
    threadId,
    sendMessage,
    hasEverMessaged,
    hasPendingEntryStarter,
    isLoadingHistory,
    isSending,
    isAwaitingReply,
    busyMessage,
    activityStatus,
    connectionState,
    isConnected,
    isGreetingPending,
    opensWithGreeting,
    reconnect,
  } = useOriChat({ enabled: hasAccepted, qa: oriQaSession.connectionParams });
  const {
    selectedDateKey,
    readOnlyMessages,
    readOnlyStartedAt,
    isLoadingConversation,
    selectConversation,
    clearSelection,
  } = useOriHistory();
  const { isEnabled } = useFeatureFlags();
  const wordedLoading = isEnabled(FEATURE_FLAGS.ORI_WORDED_LOADING);
  const { user } = useAuth();
  const firstName = user?.firstName;
  const navigate = useNavigate();
  const disclaimerVisible = !hasAccepted;
  const handleAcceptDisclaimer = useCallback(async () => {
    try {
      await acceptDisclaimer();
      // Reading the disclaimer can outlast the stashed starter's freshness
      // window; re-stamp it so a first-time user's seeded prompt still sends
      // once the socket connects.
      refreshOriStarterMessage();
    } catch {
      toast.error(t("ori:disclaimer.acceptError"));
    }
  }, [acceptDisclaimer, t]);

  // Gate on the loaded transcript so `Has Existing Messages` reflects the
  // returning-user state, not the pre-fetch empty view.
  const hasTrackedScreenView = useRef(false);
  useEffect(() => {
    if (disclaimerVisible || isLoadingHistory || hasTrackedScreenView.current)
      return;
    hasTrackedScreenView.current = true;
    trackEvent("Chat Screen Viewed", {
      "Has Existing Messages": messages.length > 0,
    });
  }, [disclaimerVisible, isLoadingHistory, messages.length]);

  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const openHistory = useCallback(() => setIsHistoryOpen(true), []);
  const closeHistory = useCallback(() => setIsHistoryOpen(false), []);

  const [isQaDrawerOpen, setIsQaDrawerOpen] = useState(false);
  const { setQaMode } = oriQaSession;
  const handleQaModeChange = useCallback(
    (on: boolean) => {
      setQaMode(on);
      if (on) {
        // Entering QA drops any read-only continuous conversation so the view
        // belongs to the QA session, not a lingering history selection.
        clearSelection();
      } else {
        // Leaving QA closes the QA drawer; entering does NOT auto-open it — the
        // user opens it from the header "QA panel" button when ready.
        setIsQaDrawerOpen(false);
      }
    },
    [setQaMode, clearSelection]
  );
  // In QA mode the chat only connects once a bounded thread is open (a config
  // is chosen AND a thread started/resumed). Until then it shows a prompt
  // instead of the message list or composer.
  const qaAwaitingThread =
    oriQaSession.qaMode && oriQaSession.connectionParams === null;

  const isReadOnly = selectedDateKey !== null;
  const viewMessages = isReadOnly ? readOnlyMessages : messages;

  const [isTabVisible, setIsTabVisible] = useState(
    document.visibilityState === "visible"
  );
  useEffect(() => {
    const onChange = () =>
      setIsTabVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onChange);
    return () => document.removeEventListener("visibilitychange", onChange);
  }, []);
  // The disclaimer modal replaces the chat until accepted, so pills behind it
  // are not seen.
  const isChatOnScreen = isTabVisible && !disclaimerVisible;

  const [hasDraft, setHasDraft] = useState(false);
  const [dismissedStarterMessageId, setDismissedStarterMessageId] = useState<
    string | null
  >(null);
  const [genericsDismissed, setGenericsDismissed] = useState(false);
  const [genericsHeld, setGenericsHeld] = useState(false);
  // Shuffled once per display, like the fixed pills they replace. Each display
  // has its own id, so its impression counts once; both are drawn afresh only
  // when the display ends, never on a dropped connection or a send in flight.
  const [genericDisplay, setGenericDisplay] = useState(() => ({
    id: 0,
    order: shuffled(GENERIC_STARTER_IDS),
  }));
  const startersEnabled =
    isEnabled(FEATURE_FLAGS.ORI_DYNAMIC_STARTERS) &&
    !isReadOnly &&
    !oriQaSession.qaMode &&
    !hasPendingEntryStarter &&
    !hasDraft;
  const coachStarters = selectDynamicStarters({
    enabled: startersEnabled,
    isLoadingHistory,
    isSending,
    messages,
    dismissedMessageId: dismissedStarterMessageId,
  });
  const genericState = coachStarters
    ? "off"
    : selectGenericStarters({
        enabled: startersEnabled,
        isLoadingHistory,
        isSending,
        isConnected,
        opensWithGreeting,
        hasEverMessaged,
        messages,
        dismissed: genericsDismissed,
        held: genericsHeld,
      });
  const showGenericStarters = genericState === "on";
  useEffect(() => {
    setGenericsHeld(genericState !== "off");
    if (genericState === "off") {
      setGenericDisplay(({ id }) => ({
        id: id + 1,
        order: shuffled(GENERIC_STARTER_IDS),
      }));
    }
  }, [genericState]);
  const genericStarters = useMemo<StarterSet>(
    () => ({
      messageId: null,
      source: "generic",
      options: genericDisplay.order.map((id) => ({
        id: `generic:${id}`,
        label: t(`ori:chat.starters.${id}.label`),
        value: t(`ori:chat.starters.${id}.message`),
        Icon: GENERIC_STARTER_ICONS[id],
      })),
    }),
    [genericDisplay.order, t]
  );
  const dynamicStarters: StarterSet | null = showGenericStarters
    ? genericStarters
    : coachStarters;
  // A heading message's id, or the generic display's, which belongs to none.
  const dynamicStartersKey = showGenericStarters
    ? `generic-${genericDisplay.id}`
    : coachStarters?.messageId ?? null;

  // The first keystroke dismisses these starters for good; a draft already
  // there when they arrive only holds them back.
  const coachStartersMessageId = coachStarters?.messageId ?? null;
  const handleDraftChange = useCallback(
    (hasText: boolean) => {
      setHasDraft(hasText);
      if (!hasText) return;
      if (showGenericStarters) setGenericsDismissed(true);
      else if (coachStartersMessageId) {
        setDismissedStarterMessageId(coachStartersMessageId);
      }
    },
    [showGenericStarters, coachStartersMessageId]
  );

  // Counts, source and ids only: option text can hold wellbeing detail and
  // never reaches analytics.
  const dynamicStartersSource = dynamicStarters?.source ?? null;
  const dynamicStartersCount = dynamicStarters?.options.length ?? 0;
  const dynamicStartersMessageId = dynamicStarters?.messageId ?? null;
  const dynamicEventBase = useMemo(
    () => ({
      "Starter Count": dynamicStartersCount,
      Source: dynamicStartersSource,
      "Thread ID": threadId,
      "Message ID": dynamicStartersMessageId,
    }),
    [
      dynamicStartersCount,
      dynamicStartersSource,
      threadId,
      dynamicStartersMessageId,
    ]
  );

  // One impression per heading message or generic display, and only while the
  // tab is visible: a set that arrives in a hidden tab is seen when the
  // learner returns. Under a greeting, the generic set waits for the thread
  // the greeting brings, so the impression joins its taps; with no greeting
  // coming there may be no thread until the learner speaks, so it counts at
  // once with none, as its taps do.
  const shownStarterKeys = useRef(new Set<string>());
  useEffect(() => {
    if (
      isChatOnScreen &&
      dynamicStartersKey &&
      (!showGenericStarters || threadId || !opensWithGreeting) &&
      !shownStarterKeys.current.has(dynamicStartersKey)
    ) {
      shownStarterKeys.current.add(dynamicStartersKey);
      trackEvent("Chat Starters Shown", dynamicEventBase);
    }
  }, [
    dynamicEventBase,
    dynamicStartersKey,
    isChatOnScreen,
    opensWithGreeting,
    showGenericStarters,
    threadId,
  ]);

  // A generic starter is none of Coach's, so only its words are sent.
  const handleDynamicStarterSelect = useCallback(
    (option: StarterPillOption, index: number) => {
      trackEvent("Chat Starter Tapped", {
        ...dynamicEventBase,
        "Starter ID": option.id ?? null,
        Position: index + 1,
      });
      sendMessage(option.value, "starter");
    },
    [dynamicEventBase, sendMessage]
  );

  // Follow streamed/appended content; key on the last message's content (not
  // just count) so appended deltas — which don't change the array length —
  // still follow.
  const lastContent = viewMessages[viewMessages.length - 1]?.content ?? "";
  const { scrollRef, showJumpToLatest, onScroll, jumpToLatest } = useScrollPin(
    `${viewMessages.length}:${lastContent.length}:${isAwaitingReply}`,
    selectedDateKey ?? "__live__"
  );

  const hasConversation = messages.length > 0 || isAwaitingReply;
  // The header carries the history control, so it must stay reachable even on
  // the live empty state; only the avatar + name collapse there.
  const showOriIdentity = isReadOnly || hasConversation;

  if (disclaimerVisible) {
    return (
      <div className={styles.container} data-testid="ori-page">
        <OriDisclaimerModal
          firstName={firstName}
          onAccept={handleAcceptDisclaimer}
          onDismiss={() => navigate("/user/home")}
          isAccepting={isAccepting}
        />
      </div>
    );
  }

  return (
    <div className={styles.container} data-testid="ori-page">
      <header className={styles.header}>
        <span
          className={clsx(
            styles.headerIdentity,
            !showOriIdentity && styles.headerIdentityHidden
          )}
          aria-hidden={!showOriIdentity}
        >
          <span className={styles.headerOrb}>
            <OriAvatar
              size={28}
              cropScale={ORI_ORB_CROP_SCALE}
              testId="ori-header-avatar"
            />
          </span>
          <span className={styles.headerName}>{t("ori:brand.name")}</span>
        </span>
        {isReadOnly && readOnlyStartedAt && (
          <span className={styles.headerDate}>
            {formatHeaderDate(readOnlyStartedAt)}
          </span>
        )}
        <span className={styles.headerActions}>
          <OriQaModeToggle
            qaMode={oriQaSession.qaMode}
            onChange={handleQaModeChange}
          />
          {oriQaSession.qaMode ? (
            <OriQaPanelButton onClick={() => setIsQaDrawerOpen(true)} />
          ) : (
            <OriHistoryButton onClick={openHistory} />
          )}
        </span>
      </header>
      {qaAwaitingThread ? (
        <div className={styles.qaEmpty} data-testid="ori-qa-awaiting-thread">
          <p>
            {oriQaSession.selectedConfigId === null
              ? "Pick a configuration in the QA panel, then start a thread."
              : "Start a new thread or resume a past one in the QA panel."}
          </p>
          <Button
            variant="secondary"
            size="small"
            onClick={() => setIsQaDrawerOpen(true)}
          >
            {t("ori:qa.openPanel")}
          </Button>
        </div>
      ) : (
        <OriMessageList
          messages={viewMessages}
          isSending={isReadOnly ? false : isSending}
          isAwaitingReply={isReadOnly ? false : isAwaitingReply}
          activityStatus={isReadOnly ? null : activityStatus}
          wordedLoading={wordedLoading}
          isLoadingHistory={
            isReadOnly ? isLoadingConversation : isLoadingHistory
          }
          // A past conversation is never greeted, so the live signal must not
          // leak into the read-only view.
          isGreetingPending={isReadOnly ? false : isGreetingPending}
          firstName={firstName}
          scrollRef={scrollRef}
          onScroll={onScroll}
          // Agenda seams belong to the live bounded thread only: a learner's
          // chat and the read-only history view never have QA connection
          // params, so they can't render one.
          showAgendaSeams={
            oriQaSession.connectionParams !== null && !isReadOnly
          }
          starterPills={
            dynamicStarters ? (
              <OriStarterPills
                options={dynamicStarters.options}
                onSelect={handleDynamicStarterSelect}
                disabled={!isConnected}
              />
            ) : null
          }
        />
      )}
      <div
        className={clsx(
          styles.composerDock,
          isReadOnly && styles.composerDockReadOnly
        )}
      >
        <div className={styles.centered}>
          {/* Pinned to the top of the dock so it rides up as the input grows. */}
          {showJumpToLatest && (
            <button
              type="button"
              className={styles.jumpToLatest}
              onClick={jumpToLatest}
              aria-label={t("ori:chat.jumpToLatest.a11y")}
              data-testid="ori-jump-to-latest"
            >
              <FiArrowDown className={styles.jumpIcon} aria-hidden />
            </button>
          )}
          {isReadOnly ? (
            <div className={styles.readOnlyDock}>
              <Button
                variant="secondary"
                onClick={clearSelection}
                data-testid="ori-new-chat"
              >
                {t("ori:chat.returnToCurrent")}
              </Button>
            </div>
          ) : qaAwaitingThread ? null : (
            <>
              {busyMessage !== null && (
                <div
                  className={styles.busyBanner}
                  data-testid="ori-busy-other-device"
                  role="status"
                >
                  <span className={styles.busyDot} aria-hidden />
                  {busyMessage}
                </div>
              )}
              <OriConnectionBanner
                connectionState={connectionState}
                reconnect={reconnect}
              />
              <OriComposer
                onSend={sendMessage}
                isConnected={isConnected}
                isSending={isSending}
                onDraftChange={handleDraftChange}
              />
            </>
          )}
        </div>
      </div>
      <OriHistoryPanel
        isOpen={isHistoryOpen}
        onClose={closeHistory}
        onSelect={selectConversation}
        selectedDateKey={selectedDateKey}
      />
      <OriQaDrawer
        isOpen={isQaDrawerOpen}
        onClose={() => setIsQaDrawerOpen(false)}
        session={oriQaSession}
        onThreadOpened={() => setIsQaDrawerOpen(false)}
      />
    </div>
  );
};

// Guards the chat route so a direct URL can't bypass the entry-point gates.
const OriPage: React.FC = () => {
  const { enabled, loading } = useWebOriEnabled();

  if (loading) return <PageLoading />;
  if (!enabled) return <Navigate to="/user/home" replace />;

  return <OriChat />;
};

export default OriPage;
